import { z } from 'zod';
import { normalizeString } from 'utils/Validations';
import type { SchedulingRule } from 'repository/schedule/@types/SchedulingRuleModel';

const GRAVITY_MIN = 0;
const GRAVITY_HOURS_MAX = 24;
const GRAVITY_PERIOD_HOURS_MAX = 24 * 7;
const GRAVITY_INTEGER_MAX = 99;

const hasAtMostTwoDecimals = (value: number): boolean => {
    return Math.abs(value * 100 - Math.round(value * 100)) < Number.EPSILON;
};

const normalizeGravityFloatValue = (value: number, maxHours: number): number => {
    const clamped = value < GRAVITY_MIN ? GRAVITY_MIN : value > maxHours ? maxHours : value;
    return Math.round(clamped * 100) / 100;
};

const normalizeGravityIntValue = (value: number): number => {
    const clamped = value < GRAVITY_MIN ? GRAVITY_MIN : value > GRAVITY_INTEGER_MAX ? GRAVITY_INTEGER_MAX : value;
    return Math.trunc(clamped);
};

const GRANULARITY_ALLOWED_VALUES = [15, 30, 60];

const GRANULARITY_TO_HOUR_PORTION: Record<number, number> = {
    15: 15,
    30: 30,
    60: 60,
};

const parseGranularity = (value: string): number => Number.parseInt(value, 10);

const toHourPortionFromGranularity = (granularityInMinutes: number): number | null => {
    return GRANULARITY_TO_HOUR_PORTION[granularityInMinutes] ?? null;
};

const toMinutesFromHourMinuteDecimal = (valueInHours: number, maxHours: number): number | null => {
    const normalizedValue = Math.round(valueInHours * 100) / 100;
    if (normalizedValue < GRAVITY_MIN || normalizedValue > maxHours) {
        return null;
    }

    return normalizedValue * 60;
};

const isMultipleOfGranularity = (valueInHours: number, granularity: number, maxHours: number): boolean => {
    if (!valueInHours) {
        return true;
    }

    const granularityHourPortion = toHourPortionFromGranularity(granularity);
    if (granularityHourPortion === null) {
        return false;
    }

    const totalMinutes = toMinutesFromHourMinuteDecimal(valueInHours, maxHours);
    if (totalMinutes === null) {
        return false;
    }

    const quotient = totalMinutes / granularityHourPortion;
    return Math.abs(quotient - Math.round(quotient)) < Number.EPSILON;
};

const isTimeMultipleOf15 = (timeValue: string): boolean => {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeValue);
    if (!match) {
        return false;
    }

    const hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2], 10);
    const totalMinutes = hours * 60 + minutes;
    return totalMinutes % 15 === 0;
};

const DURATION_TIME_REGEX = /^((?:[01]\d|2[0-3]):[0-5]\d|24:00)$/;

const parseClockTimeToMinutes = (timeValue: string): number | null => {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeValue);
    if (!match) {
        return null;
    }

    const hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2], 10);
    return hours * 60 + minutes;
};

const parseDurationToMinutes = (timeValue: string): number | null => {
    if (!DURATION_TIME_REGEX.test(timeValue)) {
        return null;
    }
    if (timeValue === '24:00') {
        return 24 * 60;
    }
    return parseClockTimeToMinutes(timeValue);
};

const getNightWindowDurationMinutes = (startTime: string, endTime: string): number | null => {
    const startMinutes = parseClockTimeToMinutes(startTime);
    const endMinutes = parseClockTimeToMinutes(endTime);
    if (startMinutes === null || endMinutes === null) {
        return null;
    }

    if (startMinutes === endMinutes) {
        return 24 * 60;
    }

    return (endMinutes - startMinutes + 24 * 60) % (24 * 60);
};

const formatDurationHoursAndMinutes = (minutes: number): string => {
    const hours = Math.trunc(minutes / 60)
        .toString()
        .padStart(2, '0');
    const mins = (minutes % 60).toString().padStart(2, '0');
    return `${hours}:${mins}`;
};

const gravityFloatRuleSchema = (maxHours: number) =>
    z.object({
        gravity: z.enum(['not_considered', 'no_safeguard', 'warning']),
        value: z
            .number()
            .refine((val) => hasAtMostTwoDecimals(val), 'Solo se permiten hasta 2 decimales (ej: 2.15, 5.30, 24.00)')
            .transform((val) => normalizeGravityFloatValue(val, maxHours)),
    });

const gravityIntRuleSchema = z.object({
    gravity: z.enum(['not_considered', 'no_safeguard', 'warning']),
    value: z
        .number()
        .int('Solo se permiten valores enteros (ej: 0, 1, 99)')
        .transform((val) => normalizeGravityIntValue(val)),
});

export function createSchedulingRuleSchema(params: { t: (key: string, options?: any) => string; rows: SchedulingRule[]; initialValues?: { name?: string }; readOnly?: boolean }) {
    const { rows, initialValues } = params;

    return z
        .object({
            // General
            name: z
                .string()
                .trim()
                .min(1, 'El nombre es requerido')
                .max(100, 'Máximo 100 caracteres')
                .refine(
                    (val) => {
                        const normalized = normalizeString(val);
                        return !rows.some((row) => {
                            if (initialValues?.name && normalizeString(initialValues.name) === normalizeString(row.name)) return false;
                            return normalizeString(row.name) === normalized;
                        });
                    },
                    { message: 'El nombre ya existe' },
                ),
            description: z.string().max(255, 'Máximo 255 caracteres'),
            weekStartDay: z.string(),
            nightStartTime: z.string(),
            nightEndTime: z.string(),
            nightLimitTime: z.string(),
            maxSegmentsPerShift: z.number().int().min(0).max(99),
            granularity: z.string().refine((val) => GRANULARITY_ALLOWED_VALUES.includes(parseGranularity(val)), 'Valor invalido. Solo se permite 15, 30 o 60.'),
            borderGranularity: z.string().refine((val) => GRANULARITY_ALLOWED_VALUES.includes(parseGranularity(val)), 'Valor invalido. Solo se permite 15, 30 o 60.'),
            shiftProfileGroupId: z.number().nullable(),
            considerPreviousPeriod: z.boolean(),
            // Horas
            minDailyWorkHours: gravityFloatRuleSchema(GRAVITY_HOURS_MAX),
            maxDailyWorkHours: gravityFloatRuleSchema(GRAVITY_HOURS_MAX),
            minPeriodWorkHours: gravityFloatRuleSchema(GRAVITY_PERIOD_HOURS_MAX),
            maxPeriodWorkHours: gravityFloatRuleSchema(GRAVITY_PERIOD_HOURS_MAX),
            minHoursBetweenShifts: gravityFloatRuleSchema(GRAVITY_HOURS_MAX),
            // Días
            minWorkDays: gravityIntRuleSchema,
            maxWorkDays: gravityIntRuleSchema,
            minConsecutiveWorkDays: gravityIntRuleSchema,
            maxConsecutiveWorkDays: gravityIntRuleSchema,
            minConsecutiveDaysOff: gravityIntRuleSchema,
            maxConsecutiveDaysOff: gravityIntRuleSchema,
            minDaysBetweenNightShifts: gravityIntRuleSchema,
            minDaysBetweenRestDays: gravityIntRuleSchema,
            // Noches
            minNightShifts: gravityIntRuleSchema,
            maxNightShifts: gravityIntRuleSchema,
            minConsecutiveNightShifts: gravityIntRuleSchema,
            maxConsecutiveNightShifts: gravityIntRuleSchema,
            minDaysBetweenGroups: gravityIntRuleSchema,
        })
        .superRefine((data, ctx) => {
            const granularity = parseGranularity(data.granularity);
            const boundaryGranularity = parseGranularity(data.borderGranularity);

            if (boundaryGranularity > granularity) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['borderGranularity'],
                    message: 'La granularidad de borde debe ser menor o igual a la granularidad.',
                });
            }

            const hoursRules: Array<{ path: [string, 'value']; value: number; maxHours: number }> = [
                { path: ['minDailyWorkHours', 'value'], value: data.minDailyWorkHours.value, maxHours: GRAVITY_HOURS_MAX },
                { path: ['maxDailyWorkHours', 'value'], value: data.maxDailyWorkHours.value, maxHours: GRAVITY_HOURS_MAX },
                { path: ['minPeriodWorkHours', 'value'], value: data.minPeriodWorkHours.value, maxHours: GRAVITY_PERIOD_HOURS_MAX },
                { path: ['maxPeriodWorkHours', 'value'], value: data.maxPeriodWorkHours.value, maxHours: GRAVITY_PERIOD_HOURS_MAX },
                { path: ['minHoursBetweenShifts', 'value'], value: data.minHoursBetweenShifts.value, maxHours: GRAVITY_HOURS_MAX },
            ];

            hoursRules.forEach((rule) => {
                if (!isMultipleOfGranularity(rule.value, granularity, rule.maxHours)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: rule.path,
                        message: `Las horas deben alinearse con la granularidad de ${granularity} minutos.`,
                    });
                }
            });

            if (!isTimeMultipleOf15(data.nightStartTime)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['nightStartTime'],
                    message: 'La hora de inicio del turno nocturno debe ser multiplo de 15 minutos.',
                });
            }

            if (!isTimeMultipleOf15(data.nightEndTime)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['nightEndTime'],
                    message: 'La hora de fin del turno nocturno debe ser multiplo de 15 minutos.',
                });
            }

            const nightLimitMinutes = parseDurationToMinutes(data.nightLimitTime);
            if (nightLimitMinutes === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['nightLimitTime'],
                    message: 'La duracion limite nocturna debe tener formato HH:MM (00:00 a 24:00).',
                });
                return;
            }

            const maxNightDurationMinutes = getNightWindowDurationMinutes(data.nightStartTime, data.nightEndTime);
            if (maxNightDurationMinutes !== null && nightLimitMinutes > maxNightDurationMinutes) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['nightLimitTime'],
                    message: `La duracion limite nocturna debe estar entre 00:00 y ${formatDurationHoursAndMinutes(maxNightDurationMinutes)}.`,
                });
            }
        });
}

export type SchedulingRuleFormValues = z.infer<ReturnType<typeof createSchedulingRuleSchema>>;

const EMPTY_GRAVITY_RULE = { gravity: 'not_considered' as const, value: 0 };

export const EMPTY_SCHEDULING_RULE: SchedulingRuleFormValues = {
    name: '',
    description: '',
    weekStartDay: '',
    nightStartTime: '00:00',
    nightEndTime: '00:00',
    nightLimitTime: '00:00',
    maxSegmentsPerShift: 0,
    granularity: '',
    borderGranularity: '',
    shiftProfileGroupId: null,
    considerPreviousPeriod: false,
    minDailyWorkHours: { ...EMPTY_GRAVITY_RULE },
    maxDailyWorkHours: { ...EMPTY_GRAVITY_RULE },
    minPeriodWorkHours: { ...EMPTY_GRAVITY_RULE },
    maxPeriodWorkHours: { ...EMPTY_GRAVITY_RULE },
    minHoursBetweenShifts: { ...EMPTY_GRAVITY_RULE },
    minWorkDays: { ...EMPTY_GRAVITY_RULE },
    maxWorkDays: { ...EMPTY_GRAVITY_RULE },
    minConsecutiveWorkDays: { ...EMPTY_GRAVITY_RULE },
    maxConsecutiveWorkDays: { ...EMPTY_GRAVITY_RULE },
    minConsecutiveDaysOff: { ...EMPTY_GRAVITY_RULE },
    maxConsecutiveDaysOff: { ...EMPTY_GRAVITY_RULE },
    minDaysBetweenNightShifts: { ...EMPTY_GRAVITY_RULE },
    minDaysBetweenRestDays: { ...EMPTY_GRAVITY_RULE },
    minNightShifts: { ...EMPTY_GRAVITY_RULE },
    maxNightShifts: { ...EMPTY_GRAVITY_RULE },
    minConsecutiveNightShifts: { ...EMPTY_GRAVITY_RULE },
    maxConsecutiveNightShifts: { ...EMPTY_GRAVITY_RULE },
    minDaysBetweenGroups: { ...EMPTY_GRAVITY_RULE },
};
